import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { loginWithPassword } from '../lib/auth';
import { Loader2 } from 'lucide-react';

export function LoginPage(): JSX.Element {
  const { isLoggedIn, login, refreshAuth, isLoading } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [signingIn, setSigningIn] = useState(false);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen" style={{ background: '#111110' }}>
        <div
          className="w-5 h-5 rounded-full border-2 border-t-transparent animate-spin"
          style={{ borderColor: '#3F3F3B', borderTopColor: '#E07B30' }}
        />
      </div>
    );
  }

  if (isLoggedIn) return <Navigate to="/dashboard" replace />;

  const handlePasswordLogin = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setError(null);
    setSigningIn(true);
    try {
      const result = await loginWithPassword(email.trim(), password);
      if (result.success) {
        refreshAuth();
        navigate('/dashboard', { replace: true });
      } else {
        setError(result.error ?? 'Sign in failed');
      }
    } finally {
      setSigningIn(false);
    }
  };

  return (
    <div
      className="flex items-center justify-center h-screen"
      style={{ background: '#111110' }}
    >
      <div
        className="w-full max-w-sm mx-4 rounded-2xl p-8"
        style={{
          background: '#1C1C1A',
          border: '1px solid #2E2E2B',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
        }}
      >
        {/* Logo + Title */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2.5 mb-2">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center"
              style={{ background: '#E07B30' }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#111110" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="20" x2="18" y2="10" />
                <line x1="12" y1="20" x2="12" y2="4" />
                <line x1="6" y1="20" x2="6" y2="14" />
              </svg>
            </div>
            <h1 className="text-xl font-semibold" style={{ color: '#EFEFED' }}>
              Sprint Pulse
            </h1>
          </div>
          <p className="text-sm" style={{ color: '#6B6962' }}>
            Sign in to continue
          </p>
        </div>

        {/* Error */}
        {error && (
          <div
            className="rounded-lg px-3 py-2.5 text-xs mb-4"
            style={{ background: '#3B1111', color: '#F87171', border: '1px solid rgba(248,113,113,0.2)' }}
          >
            {error}
          </div>
        )}

        {/* Email / Password form */}
        <form onSubmit={handlePasswordLogin} className="space-y-3 mb-5">
          <div className="relative">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              autoComplete="username"
              className="w-full px-3.5 py-2.5 rounded-lg text-sm outline-none transition-colors"
              style={{
                background: '#252523',
                border: '1px solid #2E2E2B',
                color: '#EFEFED',
              }}
              onFocus={(e) => { e.target.style.borderColor = 'rgba(224,123,48,0.5)'; }}
              onBlur={(e) => { e.target.style.borderColor = '#2E2E2B'; }}
            />
          </div>
          <div className="relative">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              autoComplete="current-password"
              className="w-full px-3.5 py-2.5 rounded-lg text-sm outline-none transition-colors"
              style={{
                background: '#252523',
                border: '1px solid #2E2E2B',
                color: '#EFEFED',
              }}
              onFocus={(e) => { e.target.style.borderColor = 'rgba(224,123,48,0.5)'; }}
              onBlur={(e) => { e.target.style.borderColor = '#2E2E2B'; }}
            />
          </div>
          <button
            type="submit"
            disabled={signingIn || !email.trim() || !password}
            className="w-full py-2.5 rounded-lg text-sm font-semibold transition-all disabled:opacity-40 flex items-center justify-center gap-2"
            style={{
              background: 'linear-gradient(135deg, #E07B30 0%, #C8691A 100%)',
              color: '#111110',
            }}
          >
            {signingIn && <Loader2 size={14} className="animate-spin" />}
            Sign In
          </button>
        </form>

        {/* Divider */}
        <div className="flex items-center gap-3 mb-5">
          <div className="flex-1 h-px" style={{ background: '#2E2E2B' }} />
          <span className="text-xs font-medium" style={{ color: '#6B6962' }}>OR</span>
          <div className="flex-1 h-px" style={{ background: '#2E2E2B' }} />
        </div>

        {/* SSO Button */}
        <button
          onClick={login}
          className="w-full py-2.5 rounded-lg text-sm font-semibold transition-all hover:opacity-90 flex items-center justify-center gap-2"
          style={{
            background: '#252523',
            border: '1px solid #4A4A46',
            color: '#EFEFED',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <rect x="1" y="1" width="10" height="10" rx="1" fill="#f25022" />
            <rect x="13" y="1" width="10" height="10" rx="1" fill="#7fba00" />
            <rect x="1" y="13" width="10" height="10" rx="1" fill="#00a4ef" />
            <rect x="13" y="13" width="10" height="10" rx="1" fill="#ffb900" />
          </svg>
          Sign in with SSO
        </button>
      </div>
    </div>
  );
}
